"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import {
  accentActiveBorder,
  accentBg,
  accentProgress,
  accentRing,
  accentSoft,
  ROTATION_MS,
  type Category,
} from "./data";

type CategoryBlockProps = {
  category: Category;
  reversed: boolean;
};

const CategoryBlock = ({ category, reversed }: CategoryBlockProps) => {
  const { t } = useTranslation();
  const Icon = category.icon;
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const videoRef = useRef<HTMLVideoElement>(null);

  const prefersReducedMotion = usePrefersReducedMotion();
  // null = no explicit choice yet, fall back to the OS-level motion preference
  const [userPaused, setUserPaused] = useState<boolean | null>(null);
  const paused = userPaused ?? prefersReducedMotion;

  useEffect(() => {
    if (!api || paused) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 3000);
    return () => clearInterval(interval);
  }, [api, paused]);

  const activeFeature = category.features[activeIndex];
  const currentDuration = activeFeature.duration || ROTATION_MS;

  useEffect(() => {
    if (paused) return;
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / currentDuration) * 100);
      setProgress(pct);
      if (elapsed >= currentDuration) {
        setProgress(0);
        setActiveIndex((i) => (i + 1) % category.features.length);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [activeIndex, category.features.length, currentDuration, paused]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (paused) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  }, [paused, activeFeature]);

  const handleSelect = (index: number) => {
    if (index === activeIndex) return;
    setProgress(0);
    setActiveIndex(index);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      {/* Video/Image block */}
      <div
        className={cn(
          "relative w-full rounded-3xl overflow-hidden border-2 border-border bg-card aspect-video ring-4",
          accentRing[category.accent],
          reversed ? "lg:order-2" : "lg:order-1",
        )}
      >
        {activeFeature.video ? (
          <video
            key={activeFeature.video}
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover animate-fade-in"
            src={activeFeature.video}
            aria-label={t(activeFeature.labelKey)}
            autoPlay={!paused}
            loop
            muted
            playsInline
          />
        ) : activeFeature.images ? (
          <Carousel
            setApi={setApi}
            className="w-full h-full"
            opts={{ loop: true }}
          >
            <CarouselContent className="h-full ml-0">
              {activeFeature.images.map((img, index) => (
                <CarouselItem key={index} className="pl-0 h-full">
                  <img
                    src={img}
                    alt={`${t(activeFeature.labelKey)} ${index + 1}`}
                    className="w-full h-full object-cover animate-fade-in"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
        <div className="absolute top-4 left-4">
          <span
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-heading font-bold",
              accentBg[category.accent],
            )}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            {t(category.labelKey)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setUserPaused(!paused)}
          aria-label={paused ? t("features.resume") : t("features.pause")}
          className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/80 text-foreground backdrop-blur-sm transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {paused ? (
            <Play className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Pause className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Text + features */}
      <div
        className={cn(
          "flex flex-col justify-center",
          reversed ? "lg:order-1" : "lg:order-2",
        )}
      >
        <h3 className="font-heading font-bold text-3xl md:text-4xl mb-4">
          {t(category.titleKey)}
        </h3>
        <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
          {t(category.descriptionKey)}
        </p>
        <ul className="space-y-3">
          {category.features.map((f, i) => {
            const FIcon = f.icon;
            const isActive = i === activeIndex;
            return (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => handleSelect(i)}
                  aria-pressed={isActive}
                  className={cn(
                    "w-full text-left flex items-start gap-3 p-4 rounded-2xl border-2 bg-card card-hover relative overflow-hidden transition-colors",
                    isActive
                      ? accentActiveBorder[category.accent]
                      : "border-border",
                  )}
                >
                  <div
                    className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
                      accentSoft[category.accent],
                    )}
                  >
                    <FIcon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div className="flex-1 pt-1.5">
                    <span className="font-medium text-foreground">{t(f.labelKey)}</span>
                  </div>
                  {/* Bottom progress indicator */}
                  <span
                    aria-hidden="true"
                    className="absolute left-0 bottom-0 h-1 bg-border/40 w-full"
                  />
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className={cn(
                        "absolute left-0 bottom-0 h-1 transition-[width] duration-100 ease-linear",
                        accentProgress[category.accent],
                      )}
                      style={{ width: `${progress}%` }}
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default CategoryBlock;
